import { c as _c } from "react/compiler-runtime";
import Image, { type StaticImageData } from 'next/image';
import { cn } from '@/lib/utils';
import { LogoBadge } from '@/components/ui/LogoBadge';

/**
 * Rounded-full project logo avatar with a monogram fallback (first char of the
 * name) shared by the /projects grid card and the home featured list. `size`
 * selects the variant: 12 = grid card (size-12, `z-10`, text-lg monogram),
 * 10 = featured list (size-10, `mt-1 flex-none`, text-sm monogram). Both share
 * the white image bg and `logoScale`, so a logo is framed identically on either
 * surface. The circular surface itself comes from LogoBadge.
 */
interface ProjectLogoProps {
  src?: string | StaticImageData;
  alt: string;
  size: 10 | 12;
  /** Scale transform for the image (`logoScale` in projectsData). */
  logoScale?: number;
}
export function ProjectLogo(t0) {
  const $ = _c(22);
  const {
    src,
    alt,
    size,
    logoScale
  } = t0;
  const isLarge = size === 12;
  const sizing = isLarge ? "z-10 size-12" : "mt-1 size-10 flex-none";
  if (!src) {
    const t1 = isLarge ? "text-lg" : "text-sm";
    let t2;
    if ($[0] !== sizing || $[1] !== t1) {
      t2 = cn("bg-zinc-100 font-semibold text-zinc-500 dark:text-zinc-400", sizing, t1);
      $[0] = sizing;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== alt) {
      t3 = alt.charAt(0);
      $[3] = alt;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t2 || $[6] !== t3) {
      t4 = <LogoBadge className={t2}>{t3}</LogoBadge>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t1;
  if ($[8] !== sizing) {
    t1 = cn("overflow-hidden dark:border dark:border-zinc-700/50", "bg-white", sizing);
    $[8] = sizing;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = logoScale ? `scale(${logoScale})` : undefined;
  let t3;
  if ($[10] !== t2) {
    t3 = {
      transform: t2
    };
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== isLarge) {
    t4 = isLarge ? {
      width: 48,
      height: 48
    } : {};
    $[12] = isLarge;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== alt || $[15] !== src || $[16] !== t3 || $[17] !== t4) {
    t5 = <Image src={src} alt={alt} className="size-full object-cover" style={t3} {...t4} unoptimized={true} />;
    $[14] = alt;
    $[15] = src;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t1 || $[20] !== t5) {
    t6 = <LogoBadge className={t1}>{t5}</LogoBadge>;
    $[19] = t1;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}